import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { godTranslations, type MangaItem } from "@/mocks/site";

const categories = ["全部", "治愈日常", "青春恋爱", "公路群像", "美食群像"];

export default function Collections() {
  const [active, setActive] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    let list: MangaItem[] = godTranslations;
    if (active !== "全部") list = list.filter((m) => m.category === active);
    if (keyword.trim()) {
      const k = keyword.trim().toLowerCase();
      list = list.filter((m) => (m.title + m.jpTitle + m.tags.join("")).toLowerCase().includes(k));
    }
    return list;
  }, [active, keyword]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-32 pb-16">
        {/* 面包屑 */}
        <nav aria-label="面包屑" className="text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
            <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
            <li aria-current="page" className="text-foreground-800">神翻译合集</li>
          </ol>
        </nav>

        <header className="mt-6 max-w-3xl">
          <p className="font-label text-sm text-primary-600">GOD TRANSLATIONS</p>
          <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-5xl">神翻译合集</h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            这里收录的是逐话对比多个版本后，在翻译准确度、润色自然度、嵌字排版、删页处理上表现优异的润色佳作。
            每一部都能让你读到原作该有的情绪与分镜，不用再为烂翻译烂嵌字心烦。
          </p>
        </header>

        {/* 筛选栏 */}
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  active === c
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <label className="flex max-w-md items-center gap-2 rounded-full bg-background-100 px-4 py-2">
            <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索作品名、标签…"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </label>
        </div>

        {/* 列表 */}
        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((m) => (
              <article
                key={m.id}
                className="group flex flex-col overflow-hidden rounded-xl bg-background-50 ring-1 ring-background-200 transition-shadow hover:shadow-lift"
              >
                <div className="relative h-64 w-full overflow-hidden">
                  <img
                    src={m.image}
                    alt={`${m.title} 神翻译收录封面`}
                    title={`${m.title} 神翻译评测`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                    评分 {m.rating}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-black/40 px-3 py-1 text-xs text-background-50 backdrop-blur-sm">
                    {m.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h2 className="font-heading text-xl font-bold text-foreground-950">{m.title}</h2>
                  <p className="mt-0.5 text-xs text-foreground-500">{m.jpTitle}</p>
                  <blockquote className="mt-3 border-l-2 border-primary-300 pl-3 text-sm italic text-primary-700">
                    「{m.quote}」
                  </blockquote>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-700">{m.desc}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {m.tags.map((t) => (
                      <span key={t} className="rounded-full bg-background-100 px-2.5 py-0.5 text-xs text-foreground-600">
                        #{t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto pt-5">
                    <p className="rounded-lg bg-secondary-100 px-3 py-2.5 text-xs text-secondary-900">
                      <span className="font-medium">阅读建议：</span>{m.note}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-xl bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-400" aria-hidden="true"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到匹配的作品，换个关键词试试吧。</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}